import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CardV2 as Card, InlineEdit } from '@feathr/components';

import AdGroupEditor from '../AdGroupEditor';
import AdGroupPreview from '../AdGroupPreview';
import type { IAd, IAdGroup, IAdGroupRowErrors, TAdGroupPatch } from '../AdStep.types';
import ExpandableSection from '../ExpandableSection';

interface IAdGroupCardProps {
  adGroup: IAdGroup;
  /** Per-row messages for this ad group, from the models' constraints. */
  rowErrors: IAdGroupRowErrors;
  /** This ad group's messages that have no field to sit against — counts and minimums. */
  adGroupErrors: string[];
  index: number;
  /** Which ad group is being edited, or null. Any non-null value locks the others. */
  editingAdGroup: number | null;
  isComplete: boolean;
  defaultOpen: boolean;
  onEdit: (index: number) => void;
  onDone: () => void;
  /** Requests deletion. The step owns the confirmation modal. */
  onRequestDelete: (index: number) => void;
  onRename: (index: number, name: string) => void;
  onChange: (index: number, patch: TAdGroupPatch) => void;
  onAddAd: (index: number) => void;
  onRemoveAd: (index: number, adIndex: number) => void;
  onUpdateAd: (index: number, adIndex: number, patch: Partial<Omit<IAd, 'key'>>) => void;
  /** The advertiser name Google shows above each ad's display URL. */
  businessName?: string;
  disabled?: boolean;
}

/**
 * One ad group as a card that expands in place. Collapsed it summarizes what's filled
 * in; in edit mode it swaps in the full editor.
 *
 * @param props - {@link IAdGroupCardProps}.
 */
function AdGroupCard({
  adGroup,
  rowErrors,
  adGroupErrors,
  index,
  editingAdGroup,
  isComplete,
  defaultOpen,
  onEdit,
  onDone,
  onRequestDelete,
  onRename,
  onChange,
  onAddAd,
  onRemoveAd,
  onUpdateAd,
  businessName,
  disabled = false,
}: Readonly<IAdGroupCardProps>): JSX.Element {
  const { t } = useTranslation();
  const isEditing = editingAdGroup === index;
  // Editing one ad group locks every other, so a half-finished edit can't be abandoned by
  // wandering into a sibling. Deleting is never blocked — step validation flags a shortfall.
  const isLockedByOtherEdit = editingAdGroup !== null && !isEditing;

  function handleEdit(): void {
    onEdit(index);
  }

  function handleRequestDelete(): void {
    onRequestDelete(index);
  }

  function handleRename(value?: string): void {
    onRename(index, value ?? '');
  }

  function handleChange(patch: TAdGroupPatch): void {
    onChange(index, patch);
  }

  function handleAddAd(): void {
    onAddAd(index);
  }

  function handleRemoveAd(adIndex: number): void {
    onRemoveAd(index, adIndex);
  }

  function handleUpdateAd(adIndex: number, patch: Partial<Omit<IAd, 'key'>>): void {
    onUpdateAd(index, adIndex, patch);
  }

  return (
    <ExpandableSection
      defaultOpen={defaultOpen}
      disabled={disabled || isLockedByOtherEdit}
      editing={isEditing}
      isComplete={isComplete}
      name={`ad-group-${index + 1}`}
      onDelete={handleRequestDelete}
      title={
        <InlineEdit
          disabled={disabled}
          onChange={handleRename}
          validationError={rowErrors.name.length ? rowErrors.name : undefined}
          value={adGroup.name}
        />
      }
    >
      {isEditing ? (
        <AdGroupEditor
          adGroup={adGroup}
          adGroupErrors={adGroupErrors}
          businessName={businessName}
          disabled={disabled}
          onAddAd={handleAddAd}
          onChange={handleChange}
          onDone={onDone}
          onRemoveAd={handleRemoveAd}
          onUpdateAd={handleUpdateAd}
          rowErrors={rowErrors}
        />
      ) : (
        <>
          <AdGroupPreview adGroup={adGroup} businessName={businessName} rowErrors={rowErrors} />
          <Card.Actions>
            <Button
              disabled={disabled || isLockedByOtherEdit}
              name={`ad-group-${index + 1}-edit`}
              onClick={handleEdit}
            >
              {t('Edit ad group')}
            </Button>
          </Card.Actions>
        </>
      )}
    </ExpandableSection>
  );
}

export default AdGroupCard;
